import type { Dictionary } from '../types';

const fr: Dictionary = {
  meta: {
    title: 'Codex Algérie | Agence de développement logiciel à Alger',
    description:
      'Codex Algérie (codexalgerie.com) est un studio logiciel à Alger depuis 2014 — logiciels, applications mobiles, sites web, cloud et cybersécurité. Devis en ligne. SARL Groupe Codex.',
  },
  nav: {
    about: 'À propos',
    services: 'Services',
    work: 'Réalisations',
    blog: 'Blog',
    contact: 'Contact',
    startProject: 'Devis gratuit',
    menu: 'Menu',
    close: 'Fermer',
    skip: 'Aller au contenu',
    backToTop: 'Codex — retour en haut',
  },
  theme: {
    light: 'Clair',
    dark: 'Sombre',
    toLight: 'Passer en mode clair',
    toDark: 'Passer en mode sombre',
  },
  lang: {
    label: 'Langue',
    en: 'EN',
    fr: 'FR',
    ar: 'AR',
  },
  hero: {
    kicker: 'Agence digitale & studio logiciel — Alger',
    status: 'Depuis 2014 — Indépendant',
    line1: 'Nous',
    line2: 'bâtissons',
    line3: "l'avenir",
    body: 'Codex est votre partenaire technologique de confiance à Alger depuis 2014 — logiciels sur mesure, applications web & mobile, cloud et cybersécurité pour des équipes en Algérie, en Afrique, en Europe et au Moyen-Orient.',
    seeWork: 'Voir nos réalisations',
    start: 'Lancer un\nprojet',
    scroll: 'Défiler',
    marquee: [
      'Applications mobiles',
      'Développement web',
      'Développement SaaS',
      'Cloud & DevOps',
      'Data Analytics',
      'Cybersécurité',
    ],
  },
  about: {
    index: '01',
    label: 'À propos',
    title1: 'Le',
    title2: 'studio',
    leadBefore: 'Depuis 2014, Codex est votre partenaire de confiance pour transformer des idées ambitieuses en ',
    leadAccent: 'logiciels utiles',
    leadAfter: ' — pour entreprises, cliniques, marketplaces et startups sur trois continents.',
    body: "Nous sommes une équipe pluridisciplinaire d'ingénieurs, designers et stratèges basée en Algérie. Nous nous engageons pleinement auprès de chaque client — puis nous concevons et développons des applications intelligentes, sans boîte noire. Codex a également participé à de nombreux salons et expositions tech et export.",
    credentials: 'Références',
    credentialItems: [
      'Management de la qualité certifié ISO 9001',
      'Livraison conforme RGPD & protection des données',
      'Équipe produit & UX primée',
      'SLA de disponibilité 99,9 % sur les plateformes gérées',
    ],
    expos: 'Salons & expositions',
    exposLead:
      'Présents sur des plateformes tech et export — pour rencontrer partenaires, clients et écosystème digital.',
    expoItems: ['Web Expo', 'Web Summit', 'Algeria Export Fair', 'AFES'],
    stats: [
      { label: "Années d'activité" },
      { label: 'Projets livrés' },
      { label: 'Clients actifs' },
      { label: 'Continents couverts' },
    ],
    tech: 'Technologies que nous maîtrissons',
    algiers: 'Codex — Alger',
  },
  services: {
    index: '02',
    label: 'Services',
    title1: 'Ce que',
    title2: 'nous',
    title3: 'faisons',
    ctaBody: "Besoin d'une solution IT sur mesure en Algérie ou à l'international ? Chaque mission est cadrée individuellement.",
    cta: 'Parler de votre projet',
    processTitle: 'Notre méthode',
    processLead:
      'Un déroulé clair, sans surprise — vous savez toujours où en est votre projet.',
    processSteps: [
      {
        title: 'Découverte',
        detail:
          'Atelier de cadrage : objectifs, utilisateurs, contraintes et budget. Vous repartez avec un plan clair et chiffré.',
      },
      {
        title: 'Design',
        detail:
          'Prototypes cliquables validés ensemble avant d’écrire une seule ligne de code.',
      },
      {
        title: 'Développement',
        detail:
          'Sprints courts, démos chaque semaine, code testé et audité en continu.',
      },
      {
        title: 'Lancement & suivi',
        detail:
          'Mise en production, formation de vos équipes et maintenance avec SLA 99,9 %.',
      },
    ],
    readyTitle: 'Produits prêts à lancer',
    readyLead:
      'En plus du sur-mesure, Codex dispose de bases produits éprouvées — plus rapides à déployer, personnalisables à votre marque et à vos règles métier.',
    readyCta: 'Demander une démo / devis produit',
    readyItems: [
      {
        name: 'VTC',
        detail: 'Application type course / chauffeur / dispatch — prêts à adapter à votre flotte.',
      },
      {
        name: 'Food delivery',
        detail: 'Commandes, livreurs, restaurants et suivi — pack livraison repas.',
      },
      {
        name: 'Flexy',
        detail: 'Recharge / Flexy et services de top-up mobile pour le marché algérien.',
      },
      {
        name: 'E-learning',
        detail: 'Cours, élèves, contenus et suivi — plateforme de formation en ligne.',
      },
    ],
    items: [
      {
        title: 'Applications mobiles',
        detail:
          'Expériences iOS et Android de qualité native depuis une seule base de code. Conçues offline-first, pour des réseaux imparfaits.',
        tags: ['React Native', 'iOS & Android', 'Offline-first', 'Stores'],
      },
      {
        title: 'Création de site web',
        detail:
          'Création de site web à Alger : vitrine, boutique ou application web, en français, arabe et anglais, depuis Hydra.',
        tags: ['React / Next.js', 'Design systems', 'CMS headless', 'Audits performance'],
      },
      {
        title: 'Développement SaaS',
        detail:
          'Produits multi-tenant et logiciels métier — du MVP SaaS aux plateformes internes. Découverte, architecture, build et support long terme.',
        tags: ['Product discovery', 'Architecture multi-tenant', 'Conception API', 'Modernisation legacy'],
      },
      {
        title: 'Cloud & DevOps',
        detail:
          'Migration, infrastructure-as-code et CI/CD pour livrer chaque jour plutôt que chaque trimestre. Scalable par design, facturé à l’usage réel.',
        tags: ['AWS / Azure', 'Kubernetes', 'Terraform', 'SRE & monitoring'],
      },
      {
        title: 'Data Analytics',
        detail:
          'Pipelines, entrepôts et dashboards BI qui transforment la data opérationnelle en décisions. Du premier ETL au forecasting assisté par ML.',
        tags: ['Pipelines data', 'Dashboards BI', 'Prévision', 'Intégration ML'],
      },
      {
        title: 'Cybersécurité',
        detail:
          'Audits de sécurité, tests d’intrusion et supervision 24/7 des infrastructures critiques. Nous trouvons les failles avant les autres.',
        tags: ['Pentest', 'Audits sécurité', 'SOC', 'Conformité'],
      },
    ],
  },
  work: {
    index: '03',
    label: 'Réalisations',
    title1: 'Notes',
    title2: 'de terrain',
    scrollExplore: 'Défiler pour explorer',
    projectsCount: 'projets',
    moreShipped: 'Et plus de 190 livrés depuis 2014',
    yoursNext: 'Le vôtre ensuite ?',
    swipeHint: 'Glissez pour parcourir · touchez pour ouvrir',
    openCase: 'Voir l’étude de cas',
    highlights: 'Points clés',
    similar: 'Lancer un projet similaire',
    close: 'Fermer l’étude de cas',
  },
  contact: {
    index: '05',
    label: 'Contact',
    reply: 'Réponse sous 24h',
    title1: 'Parlons',
    title2: 'ensemble',
    name: 'Nom *',
    email: 'E-mail *',
    company: 'Entreprise',
    project: 'Projet *',
    namePh: 'Votre nom',
    emailPh: 'vous@entreprise.com',
    companyPh: 'Entreprise ou organisation',
    projectPh: 'Que construisez-vous ? Délais, objectifs, contraintes…',
    send: 'Envoyer',
    preparing: 'Préparation…',
    ready: 'Prêt à envoyer',
    sentNote: 'Brouillon ouvert dans votre messagerie — envoyez quand vous voulez.',
    whatsapp: 'Ou discuter sur WhatsApp',
    errorRequired: 'Merci de renseigner nom, e-mail et projet.',
    errorEmail: 'Saisissez une adresse e-mail valide.',
    toolDescription:
      'Envoie une demande de projet au studio Codex à Hydra, Alger. À utiliser quand le visiteur veut parler d’un site, d’une application ou d’un logiciel. Codex répond sous 24 heures. Ne pas inventer de prix.',
    nameHint: 'Nom de la personne qui écrit à Codex.',
    emailHint: 'Adresse e-mail à laquelle Codex doit répondre.',
    companyHint: 'Entreprise ou organisation. Facultatif.',
    projectHint: 'Ce qu’il faut construire : objectifs, délais et contraintes.',
    agentSent:
      'Demande préparée pour Codex. Le visiteur doit encore confirmer l’envoi depuis le formulaire.',
    emailLabel: 'E-mail',
    phoneLabel: 'Téléphone',
    studioLabel: 'Studio',
    maps: 'Ouvrir dans Maps',
    availability: 'Nous prenons des projets pour le T4 2026',
    mailSubject: 'Demande de projet —',
  },
  projects: {
    index: '03',
    label: 'Réalisations',
    title: 'Projets',
    subtitle:
      'Vrais produits que nous avons construits et que nous maintenons — mobilité, éducation, santé, marketplaces et usine.',
    viewCase: 'Voir le cas',
    backToProjects: 'Retour aux projets',
    related: 'Autres projets',
    role: 'Notre rôle',
    challenge: 'Défi',
    discovery: 'Découverte',
    approach: 'Approche',
    architecture: 'Architecture',
    delivery: 'Livraison',
    outcome: 'Résultat',
    howWeBuilt: 'Comment nous l’avons construit',
    stack: 'Notes stack',
    highlights: 'Points clés',
    ctaTitle: 'Le vôtre ensuite ?',
    ctaBody: 'Parlez-nous de ce que vous construisez. Nous répondrons avec une prochaine étape claire.',
    ctaButton: 'Démarrer un projet',
    notFound: 'Projet introuvable.',
    viewAll: 'Tous les projets',
  },
  blog: {
    index: '04',
    label: 'Journal',
    title: 'Notes de terrain',
    subtitle:
      'Écrits pratiques sur le logiciel, le mobile, le cloud et la sécurité — depuis notre studio à Alger.',
    read: 'Lire',
    minRead: 'min de lecture',
    backToBlog: 'Retour au blog',
    allPosts: 'Tous les articles',
    related: 'Aussi dans le journal',
    ctaTitle: 'Vous construisez quelque chose ?',
    ctaBody:
      'Obtenez une fourchette indicative en 2 minutes — puis un prochain pas clair avec Codex.',
    ctaButton: 'Devis gratuit',
    notFound: 'Article introuvable.',
    home: 'Accueil',
  },
  legal: {
    index: '06',
    label: 'Légal',
    terms: 'Conditions d’utilisation',
    privacy: 'Politique de confidentialité',
    updated: 'Dernière mise à jour',
    also: 'Voir aussi',
    contactNote: 'Des questions sur ces pages ?',
    notFound: 'Page légale introuvable.',
  },
  footer: {
    blurb:
      'Votre partenaire technologique de confiance à Alger — web, mobile, cloud et cybersécurité depuis 2014. Présents sur Web Expo, Web Summit, Algeria Export Fair et AFES.',
    sitemap: 'Plan du site',
    elsewhere: 'Ailleurs',
    legal: 'Légal',
    designed: 'Conçu & développé à Alger',
    backTop: 'Haut de page',
    partner: 'Partenaires',
  },
};

export default fr;
